import { Tables } from '../../db_types'

type DiscrepancyValueLike = Pick<
  Tables<'payout_discrepancies'>,
  | 'calculated_node_output'
  | 'calculated_value'
  | 'existing_currency'
  | 'existing_original_value'
  | 'existing_value'
>

export type PreferredDiscrepancyAmount = {
  value: number
  currency: string | null
  source: 'org' | 'original'
}

export type PreferredDiscrepancyAmounts = {
  existing: PreferredDiscrepancyAmount | null
  calculated: PreferredDiscrepancyAmount
  diff: PreferredDiscrepancyAmount
}

const getNumeric = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const getCurrency = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null

const getCalculatedNodeOutput = (
  nodeOutput: Tables<'payout_discrepancies'>['calculated_node_output']
): Record<string, unknown> | null =>
  nodeOutput != null &&
  typeof nodeOutput === 'object' &&
  !Array.isArray(nodeOutput)
    ? (nodeOutput as Record<string, unknown>)
    : null

const getOrgAmount = (value: number): PreferredDiscrepancyAmount => ({
  value,
  currency: null,
  source: 'org'
})

const getOriginalAmount = (
  value: number,
  currency: string
): PreferredDiscrepancyAmount => ({
  value,
  currency,
  source: 'original'
})

export const getPreferredDiscrepancyAmounts = (
  discrepancy: DiscrepancyValueLike
): PreferredDiscrepancyAmounts => {
  const existingOrgValue = getNumeric(discrepancy.existing_value)
  const calculatedOrgValue = getNumeric(discrepancy.calculated_value) ?? 0
  const existingCurrency = getCurrency(discrepancy.existing_currency)
  const existingOriginalValue = getNumeric(discrepancy.existing_original_value)

  const calculatedNodeOutput = getCalculatedNodeOutput(
    discrepancy.calculated_node_output
  )
  const calculatedOriginalValue = getNumeric(
    calculatedNodeOutput?.original_value
  )
  const calculatedCurrency = getCurrency(calculatedNodeOutput?.currency)

  const orgDiffValue = calculatedOrgValue - (existingOrgValue ?? 0)
  const usesOriginalContext =
    existingOriginalValue != null &&
    calculatedOriginalValue != null &&
    existingCurrency != null &&
    calculatedCurrency === existingCurrency

  // Original-currency display is only safe when both the persisted "before"
  // snapshot and the recalculated "after" snapshot are complete and agree on
  // currency. Otherwise we fall back to org-currency values.
  if (usesOriginalContext) {
    return {
      existing: getOriginalAmount(existingOriginalValue, existingCurrency),
      calculated: getOriginalAmount(calculatedOriginalValue, existingCurrency),
      diff: getOriginalAmount(
        calculatedOriginalValue - existingOriginalValue,
        existingCurrency
      )
    }
  }

  return {
    existing: existingOrgValue != null ? getOrgAmount(existingOrgValue) : null,
    calculated: getOrgAmount(calculatedOrgValue),
    diff: getOrgAmount(orgDiffValue)
  }
}
